import { formatVehicleDisplayTitle } from '../components/StockVehiclePanel'
import { cotationReferenceLabel } from './cotationReference'

function urgencyFromScore(score) {
  if (score >= 700) return 'high'
  if (score >= 400) return 'medium'
  return 'low'
}

function annonceSortAt(a) {
  const raw = a?.premiere_vue_at || a?.created_date
  const t = raw ? new Date(raw).getTime() : NaN
  if (Number.isFinite(t)) return t
  const j = Number(a?.jours_en_ligne)
  if (Number.isFinite(j)) return Date.now() - j * 86400000
  return 0
}

function resolveAnnonceImage(a) {
  const candidates = [a?.image_url, a?.image_principale_url, a?.photo_url]
  for (const c of candidates) {
    const v = String(c || '').trim()
    if (v) return v
  }
  return null
}

/**
 * Met en forme un écart en CHF avec signe explicite (« +1 250 CHF » / « −800 CHF »).
 */
function fmtEcartChf(n) {
  const v = Math.abs(Math.round(Number(n) || 0))
  const sign = (Number(n) || 0) >= 0 ? '+' : '−'
  return `${sign}${v.toLocaleString('fr-CH')} CHF`
}

/**
 * FIX 2026-05 : on génère un petit "fil" de tags secondaires pour rappeler les
 * signaux qui ne sont PAS le signal dominant de la carte (ex. une moto qui
 * dort + est surcotée + a un concurrent moins cher → la carte sort en
 * "Rotation stock" mais on affiche aussi "Surcôte +11 %" et "1 concurrent
 * moins cher" en chips). Évite la répétition visuelle de cartes Rotation
 * stock qui se ressemblent toutes alors qu'elles cachent des signaux variés.
 *
 * @param {{ risk?: object, surcote?: object, souscote?: object, concSit?: string|null, nbConc?: number, ecartMedianChf?: number, ecartMedianPct?: number, exclude?: 'dormance'|'surcote'|'souscote'|'concurrence' }} opts
 */
function buildSecondaryTags(opts) {
  const tags = []
  const exclude = opts.exclude || null
  if (exclude !== 'dormance' && opts.risk) {
    const j = Number(opts.risk.jours_en_ligne) || 0
    if (j > 0) tags.push({ tone: 'dormance', label: `Dort ${j} j` })
  }
  if (exclude !== 'surcote' && opts.surcote) {
    const ec = Number(opts.surcote.ecart_pct) || 0
    if (Number.isFinite(ec) && ec !== 0) {
      tags.push({ tone: 'surcote', label: `Surcôte ${ec > 0 ? '+' : ''}${ec}%` })
    }
  }
  if (exclude !== 'souscote' && opts.souscote) {
    const ec = Number(opts.souscote.ecart_pct) || 0
    if (Number.isFinite(ec) && ec < 0) {
      tags.push({ tone: 'souscote', label: `Sous-côte ${ec}%` })
    }
  }
  if (exclude !== 'concurrence' && opts.concSit) {
    const nb = Number(opts.nbConc) || 0
    if (opts.concSit === 'critique') {
      tags.push({ tone: 'pression', label: `${nb} moins chers` })
    } else if (opts.concSit === 'expose') {
      const pct = Number.isFinite(Number(opts.ecartMedianPct)) ? Number(opts.ecartMedianPct) : null
      tags.push({
        tone: 'concurrence',
        label: pct != null ? `+${pct}% vs ${nb} concurrents` : `+ cher que ${nb} concurrents`,
      })
    } else if (opts.concSit === 'sature') {
      tags.push({ tone: 'concurrence', label: `Marché saturé (${nb})` })
    } else if (opts.concSit === 'avantage') {
      tags.push({ tone: 'souscote', label: nb > 0 ? `Le moins cher /${nb}` : 'Le moins cher' })
    }
  }
  return tags
}

/**
 * Construit la map id_source → row concurrence (`/api/concurrents/mon-stock`).
 * Tolère tableau ou objet déjà indexé.
 */
function indexConcurrenceMap(raw) {
  if (!raw) return new Map()
  if (raw instanceof Map) return raw
  const map = new Map()
  if (Array.isArray(raw)) {
    for (const row of raw) {
      const id = row?.ma_moto?.id_source
      if (id != null) map.set(String(id), row)
    }
    return map
  }
  if (typeof raw === 'object') {
    for (const [k, row] of Object.entries(raw)) {
      const id = row?.ma_moto?.id_source ?? k
      if (id != null) map.set(String(id), row)
    }
  }
  return map
}

/**
 * Actions dérivées du résultat `computeStockHealthMetrics` (Mon Stock + base Dashboard).
 * @param {object} health — retour de `computeStockHealthMetrics`
 * @param {number} seuilStockLong
 * @param {{ resolveAnnonce: (id: string|number) => object | undefined, onBaisserPrix: (annonce: object) => void, onVoirFiche: (annonce: object) => void }} handlers
 * @param {object} [concurrenceData] — map id_source → row /api/concurrents/mon-stock (ou tableau brut).
 */
export function buildStockHealthActions(health, seuilStockLong, handlers, concurrenceData = null) {
  const { resolveAnnonce, onBaisserPrix, onVoirFiche } = handlers
  const fmt = (t) => formatVehicleDisplayTitle(t)
  const concurrenceMap = indexConcurrenceMap(concurrenceData)

  // Étape 1 : on indexe les 4 signaux (dormance / surcote / souscote /
  // positionnement concurrents) par id_source. Une même moto peut figurer
  // dans plusieurs buckets (ex. elle dort ET subit une pression
  // concurrentielle) — on doit fusionner ces entrées plutôt qu'afficher
  // deux cartes presque identiques pour la même moto, qui mangent les
  // slots prioritaires du dashboard.
  const byId = new Map()
  const ensure = (id) => {
    if (!byId.has(id)) byId.set(id, { id })
    return byId.get(id)
  }

  for (const r of health.riskRows) {
    const id = String(r.id_source)
    const entry = ensure(id)
    entry.risk = r
  }
  for (const s of health.surcotes) {
    const id = String(s.id_source)
    const entry = ensure(id)
    entry.surcote = s
  }
  for (const a of health.souscotes) {
    const id = String(a.id_source)
    const entry = ensure(id)
    entry.souscote = a
  }
  // Index des annonces "non actionnables côté cotation" pour filtrer les
  // signaux concurrence (les autres signaux dépendent déjà d'une cotation
  // valide via `valeur_marche` / `ecart_pct`).
  //
  // Cas exclus :
  //   - `positionnement === 'neuf'` → moto quasi-neuve : la cotation est
  //     volontairement skippée (`skip_cotation`), on compare au catalogue
  //     constructeur, pas au marché occasion. L'utilisateur ne peut pas
  //     « ajuster le prix vs concurrents » de la même façon.
  //   - `positionnement === 'inconnu'` → cotation a échoué (pas assez de
  //     données marché). Pareil : pas d'info utile à afficher dans la fiche
  //     (« vente estimée — », « dépréciation indisponible »…), ouvrir la
  //     fiche depuis un signal concurrence donnerait l'impression que la
  //     fiche est vide.
  const isUncotable = (a) => {
    const p = a?.positionnement
    return p === 'neuf' || p === 'inconnu'
  }
  const resolveAnnonceLocal = typeof resolveAnnonce === 'function' ? resolveAnnonce : () => undefined

  for (const [id, row] of concurrenceMap.entries()) {
    const sit = row?.concurrence?.situation
    if (!sit || sit === 'dans_le_marche' || sit === 'exclusivite') continue
    // FIX 2026-05 : les situations "sature"/"avantage" n'apportent pas
    // d'action prioritaire côté concessionnaire (signal informatif). Elles
    // noyaient la file "À traiter maintenant" au détriment des leviers stock
    // concrets (dormance, surcote, pression critique). On les retire de la
    // génération d'actions de queue.
    if (sit === 'sature' || sit === 'avantage') continue
    const annonce = resolveAnnonceLocal(String(id))
    if (!annonce) continue // pas dans le stock côté front → on ne sait pas
    if (isUncotable(annonce)) continue
    const entry = ensure(String(id))
    entry.concurrenceRow = row
  }

  // Étape 2 : pour chaque moto, on choisit le signal dominant (priorité :
  // concurrence locale critique > pression concurrentielle (surcote ≥10 refs) >
  // dormance > concurrence locale "expose"/"sature" > surcote sans pression >
  // souscote / avantage concurrents). Le reste est ajouté en sous-titre.
  const items = []
  for (const entry of byId.values()) {
    const { risk, surcote, souscote, concurrenceRow } = entry
    const id = String(entry.id)

    const surcoteRefs = surcote ? Number(surcote.nb_annonces_reference) || 0 : 0
    const isCompetition = !!surcote && surcoteRefs >= 10

    const concSit = concurrenceRow?.concurrence?.situation || null
    const nbConc = Number(concurrenceRow?.concurrence?.nb_concurrents) || 0
    const ecartMedianChf = Number(concurrenceRow?.concurrence?.ecart_vs_median_chf)
    const ecartMedianPct = Number(concurrenceRow?.concurrence?.ecart_vs_median_pct)
    const prixMinConc = Number(concurrenceRow?.concurrence?.prix_min_concurrent)
    const mineTitre = concurrenceRow?.ma_moto?.titre || concurrenceRow?.ma_moto?.model

    // Helper conservé uniquement pour le sous-titre du cas A (dormance en
    // complément). Les autres signaux sont désormais affichés en chips via
    // `secondaryTags` (`buildSecondaryTags`).
    const extrasDormance = () => {
      if (!risk) return null
      const dep = Number(risk.depreciation_par_jour) || 0
      const jr = Number(risk.jours_en_ligne) || 0
      return `dort depuis ${jr} j · −${dep.toLocaleString('fr-CH')} CHF/j`
    }

    // CAS A — Concurrence locale "critique" : un ou plusieurs concurrents
    // directs sont nettement moins chers (≥2 500 CHF) → action prioritaire.
    if (concSit === 'critique') {
      const annonce = resolveAnnonce(id) || concurrenceRow?.ma_moto
      const titre = mineTitre || annonce?.titre || annonce?.model
      const subtitleLines = [
        `${nbConc} concurrent${nbConc > 1 ? 's' : ''} · médiane ${fmtEcartChf(ecartMedianChf)}` +
          (Number.isFinite(prixMinConc) ? ` · min concurrent ${prixMinConc.toLocaleString('fr-CH')} CHF` : ''),
      ]
      const extraDorm = extrasDormance()
      if (extraDorm) subtitleLines.push(`+ ${extraDorm}`)
      items.push({
        id: `merged-${id}`,
        type: 'competitor_alert',
        // FIX 2026-05 : nouvelle catégorie visuelle "pression" (orange/rouge)
        // distincte de la catégorie "concurrence" (bleue, signal modéré).
        categoryKey: 'pression',
        priorityRank: 0,
        title: `${fmt(titre)} — pression forte concurrents`,
        subtitle: subtitleLines.join('\n'),
        urgency: 'high',
        secondaryTags: buildSecondaryTags({ risk, surcote, souscote, exclude: 'concurrence' }),
        sortAt: annonceSortAt(annonce),
        imageUrl: resolveAnnonceImage(annonce),
        primaryCTA: {
          label: 'Ajuster le prix',
          onClick: () => onBaisserPrix(annonce),
        },
        secondaryCTA: {
          label: 'Voir la fiche',
          onClick: () => onVoirFiche(annonce),
        },
      })
      continue
    }

    if (isCompetition) {
      // CAS B : pression concurrentielle issue de la cotation officielle
      // (≥10 références de cotation, signal plus large que la concurrence
      // active locale). Reste un signal prioritaire.
      const refs = surcoteRefs
      const ec = Number(surcote.ecart_pct) || 0
      const j = Number(surcote.jours_en_ligne) || 0
      const annonce = resolveAnnonce(id)
      items.push({
        id: `merged-${id}`,
        type: 'competitor_alert',
        categoryKey: 'pression',
        priorityRank: 1,
        title: `${fmt(surcote.titre || surcote.model)} — pression marché (${refs} refs)`,
        subtitle:
          `${cotationReferenceLabel(surcote) || 'vs marché'} · Écart ${ec > 0 ? '+' : ''}${ec}% · ${j} j en ligne`,
        urgency: 'high',
        secondaryTags: buildSecondaryTags({
          risk,
          souscote,
          concSit,
          nbConc,
          ecartMedianChf,
          ecartMedianPct,
          exclude: 'surcote',
        }),
        sortAt: annonceSortAt(annonce || surcote),
        imageUrl: resolveAnnonceImage(annonce || surcote),
        primaryCTA: {
          label: 'Ajuster le prix',
          onClick: () => onBaisserPrix(annonce || surcote),
        },
        secondaryCTA: {
          label: 'Voir la fiche',
          onClick: () => onVoirFiche(annonce || surcote),
        },
        expandedContent: surcote.analyse_ia ? (
          <p style={{ margin: 0, fontSize: 12, color: 'rgba(203,213,225,0.85)', lineHeight: 1.5 }}>{surcote.analyse_ia}</p>
        ) : undefined,
      })
      continue
    }

    // CAS C/D fusionné : dormance OU situation expose.
    // Si le véhicule a les deux signaux (dort + plus cher que concurrents),
    // le signal dominant est déterminé par la combinaison :
    //   - dormance seule → "Rotation stock"
    //   - expose seule   → "Concurrence"
    //   - dormance + expose → "Concurrence" (l'écart prix explique la dormance,
    //     le levier actionnable est l'ajustement tarifaire face aux concurrents)
    //   - dormance + surcote (sans expose) → "Rotation stock" avec chip surcôte
    if (risk || concSit === 'expose') {
      const hasExpose = concSit === 'expose'
      const dormOnly = !!risk && !hasExpose

      const dep = Number(risk?.depreciation_par_jour) || 0
      const p30 = Number(risk?.perte_estimee_30j) || 0
      const j = Number(risk?.jours_en_ligne) || 0
      const dormScore = 1000 + p30 + j * 3
      const annonce = risk ? resolveAnnonce(id) : (resolveAnnonce(id) || concurrenceRow?.ma_moto)
      const titre = (risk?.titre || risk?.model) || (mineTitre || annonce?.titre || annonce?.model)

      if (dormOnly) {
        // Dormance seule — mais si la moto est AUSSI surcotée, on affiche la
        // surcôte comme signal dominant (c'est souvent la cause de la dormance).
        const dormHasSurcote = !!surcote
        const ec = dormHasSurcote ? (Number(surcote.ecart_pct) || 0) : 0
        const catKey = dormHasSurcote ? 'surcote' : 'dormance'
        const dormTitle = dormHasSurcote
          ? `${fmt(titre)} surcotée depuis ${j} j`
          : `${fmt(titre)} dort depuis ${j} j`
        const dormSubtitle = dormHasSurcote
          ? `Écart ${ec > 0 ? '+' : ''}${ec}% vs marché · Perte −${p30.toLocaleString('fr-CH')} CHF/30 j`
          : `Perte estimée ${dep.toLocaleString('fr-CH')} CHF/j · −${p30.toLocaleString('fr-CH')} CHF sur 30 j`
        items.push({
          id: `merged-${id}`,
          type: 'risk',
          categoryKey: catKey,
          priorityRank: dormHasSurcote ? 2 : 3,
          title: dormTitle,
          subtitle: dormSubtitle,
          urgency: urgencyFromScore(dormScore),
          secondaryTags: buildSecondaryTags({
            surcote: dormHasSurcote ? null : surcote,
            souscote,
            concSit,
            nbConc,
            ecartMedianChf,
            ecartMedianPct,
            risk: dormHasSurcote ? risk : null,
            exclude: dormHasSurcote ? 'surcote' : 'dormance',
          }),
          sortAt: annonceSortAt(annonce || risk),
          imageUrl: resolveAnnonceImage(annonce || risk),
          primaryCTA: {
            label: dormHasSurcote ? 'Baisser le prix' : 'Baisser le prix',
            onClick: () => onBaisserPrix(annonce || risk),
          },
          secondaryCTA: {
            label: 'Voir la fiche',
            onClick: () => onVoirFiche(annonce || risk),
          },
          expandedContent: (risk.analyse_ia || risk.positionnement) ? (
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(203,213,225,0.85)', lineHeight: 1.5 }}>
              {risk.analyse_ia || risk.positionnement}
            </p>
          ) : undefined,
        })
      } else {
        // Exposé (concurrent + cher) — avec ou sans dormance
        const exposéSubtitle = [
          `${nbConc} concurrent${nbConc > 1 ? 's' : ''} actif${nbConc > 1 ? 's' : ''} · médiane ${fmtEcartChf(ecartMedianChf)}` +
            (Number.isFinite(ecartMedianPct) ? ` (${ecartMedianPct >= 0 ? '+' : ''}${ecartMedianPct}%)` : ''),
        ]
        if (risk) {
          exposéSubtitle.push(`Dort depuis ${j} j · perte −${p30.toLocaleString('fr-CH')} CHF/30 j`)
        }
        items.push({
          id: `merged-${id}`,
          type: 'competitor_alert',
          categoryKey: 'concurrence',
          priorityRank: risk ? 2.5 : 3, // avec dormance : remonte légèrement dans la file
          title: `${fmt(titre)} — prix exposé ${nbConc > 1 ? `vs ${nbConc} concurrents` : 'vs concurrent'}`,
          subtitle: exposéSubtitle.join('\n'),
          urgency: risk ? 'high' : 'medium',
          secondaryTags: buildSecondaryTags({ risk, surcote, souscote, exclude: 'concurrence' }),
          sortAt: annonceSortAt(annonce || risk),
          imageUrl: resolveAnnonceImage(annonce || risk),
          primaryCTA: {
            label: 'Ajuster le prix',
            onClick: () => onBaisserPrix(annonce || risk),
          },
          secondaryCTA: {
            label: 'Voir la fiche',
            onClick: () => onVoirFiche(annonce || risk),
          },
        })
      }
      continue
    }

    // CAS E : situation concurrentielle "sature" — 5+ concurrents directs.
    // Pas d'écart prix marquant, mais densité forte → visibilité réduite.
    if (concSit === 'sature') {
      const annonce = resolveAnnonce(id) || concurrenceRow?.ma_moto
      const titre = mineTitre || annonce?.titre || annonce?.model
      items.push({
        id: `merged-${id}`,
        type: 'competitor_alert',
        categoryKey: 'concurrence',
        priorityRank: 7,
        title: `${fmt(titre)} — marché saturé`,
        subtitle:
          `${nbConc} annonces concurrentes en ligne` +
          (Number.isFinite(ecartMedianChf) ? ` · ${fmtEcartChf(ecartMedianChf)} vs médiane` : '') +
          `\nMarché saturé — peu de levier prix immédiat`,
        urgency: 'low',
        secondaryTags: buildSecondaryTags({ risk, surcote, souscote, exclude: 'concurrence' }),
        sortAt: annonceSortAt(annonce),
        imageUrl: resolveAnnonceImage(annonce),
        primaryCTA: {
          label: 'Voir la fiche',
          onClick: () => onVoirFiche(annonce),
        },
      })
      continue
    }

    if (surcote) {
      // CAS F : surcote sans pression concurrentielle (< 10 refs).
      const ec = Number(surcote.ecart_pct) || 0
      const j = Number(surcote.jours_en_ligne) || 0
      const score = 620 + Math.abs(ec) * 12 + j * 2
      const annonce = resolveAnnonce(id)
      items.push({
        id: `merged-${id}`,
        type: 'risk',
        categoryKey: 'surcote',
        priorityRank: 4,
        title: `${fmt(surcote.titre || surcote.model)} surcotée vs marché`,
        subtitle: `${cotationReferenceLabel(surcote) || 'vs marché'} · Écart ${ec > 0 ? '+' : ''}${ec}% · ${j} j en ligne`,
        urgency: urgencyFromScore(score),
        secondaryTags: buildSecondaryTags({ risk, souscote, concSit, nbConc, ecartMedianChf, ecartMedianPct, exclude: 'surcote' }),
        sortAt: annonceSortAt(annonce || surcote),
        imageUrl: resolveAnnonceImage(annonce || surcote),
        primaryCTA: {
          label: 'Baisser le prix',
          onClick: () => onBaisserPrix(annonce || surcote),
        },
        secondaryCTA: {
          label: 'Voir la fiche',
          onClick: () => onVoirFiche(annonce || surcote),
        },
        expandedContent: surcote.analyse_ia ? (
          <p style={{ margin: 0, fontSize: 12, color: 'rgba(203,213,225,0.85)', lineHeight: 1.5 }}>{surcote.analyse_ia}</p>
        ) : undefined,
      })
      continue
    }

    if (souscote) {
      // CAS G : opportunité reprise (sous-cotée vs cotation officielle).
      const ec = Number(souscote.ecart_pct) || 0
      const score = 400 + Math.abs(ec) * 6
      const annonce = resolveAnnonce(id)
      items.push({
        id: `merged-${id}`,
        type: 'opportunity',
        categoryKey: 'souscote',
        priorityRank: 5,
        title: `${fmt(souscote.titre || souscote.model)} — marge reprise`,
        subtitle: `Sous-cotée de ${ec}% ${cotationReferenceLabel(souscote) || 'vs marché'}`,
        urgency: urgencyFromScore(score),
        secondaryTags: buildSecondaryTags({ risk, surcote, concSit, nbConc, ecartMedianChf, ecartMedianPct, exclude: 'souscote' }),
        sortAt: annonceSortAt(annonce || souscote),
        imageUrl: resolveAnnonceImage(annonce || souscote),
        primaryCTA: {
          label: 'Voir dans Mon stock',
          onClick: () => onVoirFiche(annonce || souscote),
        },
        secondaryCTA: {
          label: 'Voir la fiche',
          onClick: () => onVoirFiche(annonce || souscote),
        },
        expandedContent: souscote.analyse_ia ? (
          <p style={{ margin: 0, fontSize: 12, color: 'rgba(203,213,225,0.85)', lineHeight: 1.5 }}>{souscote.analyse_ia}</p>
        ) : undefined,
      })
      continue
    }

    // CAS H : "avantage" — la moto est strictement moins chère que les
    // concurrents directs (≥ 800 CHF sous la médiane).
    if (concSit === 'avantage') {
      const annonce = resolveAnnonce(id) || concurrenceRow?.ma_moto
      const titre = mineTitre || annonce?.titre || annonce?.model
      items.push({
        id: `merged-${id}`,
        type: 'opportunity',
        categoryKey: 'souscote',
        priorityRank: 6,
        title: `${fmt(titre)} — vente rapide possible`,
        subtitle:
          `Moins cher que ${nbConc} concurrent${nbConc > 1 ? 's' : ''}` +
          (Number.isFinite(ecartMedianChf) ? ` · ${fmtEcartChf(ecartMedianChf)} sous la médiane` : ''),
        urgency: 'medium',
        secondaryTags: buildSecondaryTags({ risk, surcote, souscote, exclude: 'souscote' }),
        sortAt: annonceSortAt(annonce),
        imageUrl: resolveAnnonceImage(annonce),
        primaryCTA: {
          label: 'Voir la fiche',
          onClick: () => onVoirFiche(annonce),
        },
      })
    }
  }

  return items
}

/**
 * Opportunités marché → actions (Dashboard).
 * @param {object[]} opportunites
 * @param {{ onVoirMarche: () => void, onOpenAnnonceUrl: (url: string) => void }} handlers
 */
export function buildMarketOpportunityActions(opportunites, handlers) {
  const { onVoirMarche, onOpenAnnonceUrl } = handlers
  const fmt = (t) => formatVehicleDisplayTitle(t)
  const items = []
  for (const o of opportunites) {
    const id = String(o.id_source)
    const ec = Number(o.ecart_pct) || 0
    const px = Number(o.prix_chf) || 0
    const score = 360 + Math.abs(ec) * 10
    const url = o.url ? String(o.url) : ''
    const sortRaw = o.premiere_vue_at || o.created_date
    const sortAt = sortRaw ? new Date(sortRaw).getTime() : Date.now()
    items.push({
      id: `opp-${id}`,
      type: 'opportunity',
      categoryKey: 'marche',
      priorityRank: 8,
      title: fmt(o.titre || o.model || 'Annonce'),
      subtitle: `Écart cotation ${ec.toFixed(1)}% · ${px.toLocaleString('fr-CH')} CHF${cotationReferenceLabel(o) ? ` · ${cotationReferenceLabel(o)}` : ''}`,
      urgency: urgencyFromScore(score),
      sortAt,
      imageUrl: resolveAnnonceImage(o),
      primaryCTA: {
        label: 'Voir le marché',
        onClick: onVoirMarche,
      },
      secondaryCTA: url
        ? {
            label: "Ouvrir l'annonce",
            onClick: () => onOpenAnnonceUrl(url),
          }
        : undefined,
      expandedContent:
        o.ville && o.canton ? (
          <p style={{ margin: 0, fontSize: 12, color: 'rgba(203,213,225,0.85)', lineHeight: 1.5 }}>
            {o.ville} ({o.canton})
          </p>
        ) : o.canton ? (
          <p style={{ margin: 0, fontSize: 12, color: 'rgba(203,213,225,0.85)', lineHeight: 1.5 }}>{o.canton}</p>
        ) : undefined,
    })
  }
  return items
}
